import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  CircularProgress,
  TableContainer,
  Switch,
  FormControlLabel
} from '@mui/material';
import { apiRequest } from '../utils/api';
import { usePrivacyMask } from '../contexts/PrivacyMaskContext';

const pickPreferredQuoteAccount = (accounts) => {
  const list = Array.isArray(accounts) ? accounts : [];
  const tradier = list.find(acc => String(acc.apiType).toLowerCase() === 'tradier');
  if (tradier) return tradier;
  const robin = list.find(acc => ['robinhood', 'robinhood-api'].includes(String(acc.apiType).toLowerCase()));
  if (robin) return robin;
  return list[0] || null;
};

const normalizeSymbolsForProvider = (symbols) => {
  return symbols.map(s => {
    const m = String(s).toUpperCase().trim().match(/^([A-Z]+)\s+([A-Z])$/);
    if (m) return `${m[1]}.${m[2]}`;
    return String(s).toUpperCase().trim();
  });
};

const formatCurrencyValue = (n, privacyMask = false) => {
  if (privacyMask) return '••••••';
  const x = Number(n) || 0;
  return x.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
};

const formatPercent = (n) => {
  const x = Number(n) || 0;
  return `${x.toFixed(2)}%`;
};

const WatchlistPage = () => {
  const navigate = useNavigate();
  const { privacyMask } = usePrivacyMask();
  const formatCurrency = (value) => formatCurrencyValue(value, privacyMask);
  const [accounts, setAccounts] = useState([]);
  const [tickers, setTickers] = useState([]);
  const [quotes, setQuotes] = useState({});
  const [performance, setPerformance] = useState({});
  const [loading, setLoading] = useState(false);
  const [orderBy, setOrderBy] = useState('symbol');
  const [order, setOrder] = useState('asc');
  const [showPercent, setShowPercent] = useState(false);

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const resp = await apiRequest('/api/accounts?include_inactive=true');
        const data = resp.data.value || resp.data || [];
        setAccounts(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error('Failed to fetch accounts:', e);
      }
    };
    fetchAccounts();
  }, []);

  useEffect(() => {
    const loadTickers = async () => {
      try {
        const resp = await apiRequest('/api/watchlist');
        const symbols = resp.data?.symbols || [];
        setTickers(Array.isArray(symbols) ? symbols : []);
      } catch (e) {
        console.error('Failed to load watchlist:', e);
      }
    };
    loadTickers();
  }, []);

  useEffect(() => {
    const loadData = async () => {
      if (!tickers.length) {
        setQuotes({});
        setPerformance({});
        return;
      }
      setLoading(true);
      try {
        await fetchQuotes();
        await fetchPerformanceForTickers();
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [tickers, accounts]);

  const fetchQuotes = async () => {
    if (!tickers.length) {
      setQuotes({});
      return;
    }
    const preferred = pickPreferredQuoteAccount(accounts);
    if (!preferred || !preferred.id) {
      setQuotes({});
      return;
    }
    try {
      const symbolsForProvider = normalizeSymbolsForProvider(tickers);
      let resp;
      if (['robinhood', 'robinhood-api'].includes(String(preferred.apiType).toLowerCase())) {
        resp = await apiRequest(`/api/robinhood-api/quotes/${preferred.id}`, {
          method: 'POST',
          body: JSON.stringify({ symbols: symbolsForProvider })
        });
      } else {
        resp = await apiRequest(`/api/accounts/${preferred.id}/quotes`, {
          method: 'POST',
          body: JSON.stringify({ symbols: symbolsForProvider })
        });
      }
      const qData = resp.data?.quotes?.quote || resp.data?.quotes?.quotes?.quote || [];
      const arr = Array.isArray(qData) ? qData : [qData];
      const bySymbol = {};
      arr.forEach(q => {
        if (q && q.symbol) bySymbol[q.symbol] = q;
      });
      const out = {};
      tickers.forEach(orig => {
        const norm = normalizeSymbolsForProvider([orig])[0];
        const q = bySymbol[norm];
        const last = q ? (q.last || q.price || 0) : 0;
        const dayPct =
          q && (q.change_percentage !== undefined || q.netPercentChange !== undefined)
            ? (Number(q.change_percentage ?? q.netPercentChange) || 0)
            : 0;
        const high52 = q
          ? q.week_52_high ||
            q['week_52_high'] ||
            q['52WkHigh'] ||
            q['52WeekHigh'] ||
            q.high_52_weeks ||
            0
          : 0;
        const low52 = q
          ? q.week_52_low ||
            q['week_52_low'] ||
            q['52WkLow'] ||
            q['52WeekLow'] ||
            q.low_52_weeks ||
            0
          : 0;
        out[orig] = {
          symbol: orig,
          last: Number(last) || 0,
          dayPct: Number(dayPct) || 0,
          week52High: Number(high52) || 0,
          week52Low: Number(low52) || 0
        };
      });
      setQuotes(out);
    } catch (e) {
      console.error('Failed to fetch quotes for watchlist:', e);
      setQuotes({});
    }
  };

  const fetchPerformanceForTickers = async () => {
    if (!tickers.length) {
      setPerformance({});
      return;
    }
    const results = {};
    await Promise.all(
      tickers.map(async (sym) => {
        const metrics = { 
          w1Value: 0, w1Pct: 0,
          m1Value: 0, m1Pct: 0,
          d90Value: 0, d90Pct: 0,
          m6Value: 0, m6Pct: 0,
          y1Value: 0, y1Pct: 0
        };
        try {
          const encoded = encodeURIComponent(sym);
          const res = await apiRequest(`/api/stocks/${encoded}/history?range=1y&interval=1d`).catch(() => null);
          const candles = Array.isArray(res?.data?.candles) ? res.data.candles : [];
          const today = new Date();
          const fmt = (d) => {
            const yyyy = d.getFullYear();
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const dd = String(d.getDate()).padStart(2, '0');
            return `${yyyy}-${mm}-${dd}`;
          };
          const dMinusDays = (days) => {
            const d = new Date(today);
            d.setDate(d.getDate() - days);
            return fmt(d);
          };
          const startDates = {
            w1: dMinusDays(7),
            m1: dMinusDays(30),
            d90: dMinusDays(90),
            m6: dMinusDays(182),
            y1: dMinusDays(365)
          };
          const findCloseOnOrAfter = (start) => {
            for (let i = 0; i < candles.length; i++) {
              const c = candles[i];
              if (c.date >= start && c.close != null) return Number(c.close) || 0;
            }
            return candles.length ? Number(candles[0].close) || 0 : 0;
          };
          const last = Number((quotes[sym]?.last) || (candles.length ? candles[candles.length - 1].close : 0)) || 0;
          const baseW1 = findCloseOnOrAfter(startDates.w1);
          const baseM1 = findCloseOnOrAfter(startDates.m1);
          const baseD90 = findCloseOnOrAfter(startDates.d90);
          const baseM6 = findCloseOnOrAfter(startDates.m6);
          const baseY1 = findCloseOnOrAfter(startDates.y1);
          metrics.w1Value = last - baseW1;
          metrics.m1Value = last - baseM1;
          metrics.d90Value = last - baseD90;
          metrics.m6Value = last - baseM6;
          metrics.y1Value = last - baseY1;
          metrics.w1Pct = baseW1 ? (metrics.w1Value / baseW1) * 100 : 0;
          metrics.m1Pct = baseM1 ? (metrics.m1Value / baseM1) * 100 : 0;
          metrics.d90Pct = baseD90 ? (metrics.d90Value / baseD90) * 100 : 0;
          metrics.m6Pct = baseM6 ? (metrics.m6Value / baseM6) * 100 : 0;
          metrics.y1Pct = baseY1 ? (metrics.y1Value / baseY1) * 100 : 0;
        } catch (e) {
          console.error('Error computing price change for', sym, e);
        }
        results[sym] = metrics;
      })
    );
    setPerformance(results);
  };

  const rows = tickers.map((sym) => {
    const q = quotes[sym] || {};
    const p = performance[sym] || {};
    return {
      symbol: sym,
      last: Number(q.last || 0),
      dayPct: Number(q.dayPct || 0),
      w1Value: Number(p.w1Value || 0),
      w1Pct: Number(p.w1Pct || 0),
      m1Value: Number(p.m1Value || 0),
      m1Pct: Number(p.m1Pct || 0),
      d90Value: Number(p.d90Value || 0),
      d90Pct: Number(p.d90Pct || 0),
      m6Value: Number(p.m6Value || 0),
      m6Pct: Number(p.m6Pct || 0),
      y1Value: Number(p.y1Value || 0),
      y1Pct: Number(p.y1Pct || 0),
      week52High: Number(q.week52High || 0),
      week52Low: Number(q.week52Low || 0)
    };
  });

  const comparator = (a, b) => {
    const dir = order === 'asc' ? 1 : -1;
    if (orderBy === 'symbol') return a.symbol.localeCompare(b.symbol) * dir;
    return (a[orderBy] - b[orderBy]) * dir;
  };

  const sortedRows = [...rows].sort(comparator);

  const handleSort = (field) => {
    if (orderBy === field) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(field);
      setOrder('asc');
    }
  };

  return (
    <Box>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={showPercent}
                  onChange={(e) => setShowPercent(e.target.checked)}
                  color="primary"
                />
              }
              label={showPercent ? 'Show %' : 'Show Δ'}
            />
          </Box>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: 'calc(100vh - 220px)' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sortDirection={orderBy === 'symbol' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'symbol'}
                      direction={orderBy === 'symbol' ? order : 'asc'}
                      onClick={() => handleSort('symbol')}
                    >
                      Symbol
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sortDirection={orderBy === 'last' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'last'}
                      direction={orderBy === 'last' ? order : 'asc'}
                      onClick={() => handleSort('last')}
                    >
                      Last Price
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sortDirection={orderBy === 'dayPct' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'dayPct'}
                      direction={orderBy === 'dayPct' ? order : 'asc'}
                      onClick={() => handleSort('dayPct')}
                    >
                      Day %
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sortDirection={orderBy === (showPercent ? 'w1Pct' : 'w1Value') ? order : false}>
                    <TableSortLabel
                      active={orderBy === (showPercent ? 'w1Pct' : 'w1Value')}
                      direction={orderBy === (showPercent ? 'w1Pct' : 'w1Value') ? order : 'asc'}
                      onClick={() => handleSort(showPercent ? 'w1Pct' : 'w1Value')}
                    >
                      {showPercent ? '1 Week %' : '1 Week Δ'}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sortDirection={orderBy === (showPercent ? 'm1Pct' : 'm1Value') ? order : false}>
                    <TableSortLabel
                      active={orderBy === (showPercent ? 'm1Pct' : 'm1Value')}
                      direction={orderBy === (showPercent ? 'm1Pct' : 'm1Value') ? order : 'asc'}
                      onClick={() => handleSort(showPercent ? 'm1Pct' : 'm1Value')}
                    >
                      {showPercent ? '1 Month %' : '1 Month Δ'}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sortDirection={orderBy === (showPercent ? 'd90Pct' : 'd90Value') ? order : false}>
                    <TableSortLabel
                      active={orderBy === (showPercent ? 'd90Pct' : 'd90Value')}
                      direction={orderBy === (showPercent ? 'd90Pct' : 'd90Value') ? order : 'asc'}
                      onClick={() => handleSort(showPercent ? 'd90Pct' : 'd90Value')}
                    >
                      {showPercent ? '90 Day %' : '90 Day Δ'}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sortDirection={orderBy === (showPercent ? 'm6Pct' : 'm6Value') ? order : false}>
                    <TableSortLabel
                      active={orderBy === (showPercent ? 'm6Pct' : 'm6Value')}
                      direction={orderBy === (showPercent ? 'm6Pct' : 'm6Value') ? order : 'asc'}
                      onClick={() => handleSort(showPercent ? 'm6Pct' : 'm6Value')}
                    >
                      {showPercent ? '6 Month %' : '6 Month Δ'}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sortDirection={orderBy === (showPercent ? 'y1Pct' : 'y1Value') ? order : false}>
                    <TableSortLabel
                      active={orderBy === (showPercent ? 'y1Pct' : 'y1Value')}
                      direction={orderBy === (showPercent ? 'y1Pct' : 'y1Value') ? order : 'asc'}
                      onClick={() => handleSort(showPercent ? 'y1Pct' : 'y1Value')}
                    >
                      {showPercent ? '1 Year %' : '1 Year Δ'}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sortDirection={orderBy === 'week52High' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'week52High'}
                      direction={orderBy === 'week52High' ? order : 'asc'}
                      onClick={() => handleSort('week52High')}
                    >
                      52 Week High
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sortDirection={orderBy === 'week52Low' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'week52Low'}
                      direction={orderBy === 'week52Low' ? order : 'asc'}
                      onClick={() => handleSort('week52Low')}
                    >
                      52 Week Low
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={12} align="center">
                      No watchlist symbols found.
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedRows.map((row) => (
                    <TableRow key={row.symbol}>
                      <TableCell>
                        <Typography
                          component="span"
                          sx={{ fontWeight: 600, color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                          title="Open Stock Analysis"
                          onClick={() => navigate(`/stock-analysis/${encodeURIComponent(row.symbol)}`)}
                        >
                          {row.symbol}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{formatCurrency(row.last)}</TableCell>
                      <TableCell align="right" sx={{ color: row.dayPct > 0 ? 'success.main' : row.dayPct < 0 ? 'error.main' : 'text.primary' }}>
                        {formatPercent(row.dayPct)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: (showPercent ? row.w1Pct : row.w1Value) > 0 ? 'success.main' : (showPercent ? row.w1Pct : row.w1Value) < 0 ? 'error.main' : 'text.primary' }}>
                        {showPercent ? formatPercent(row.w1Pct) : formatCurrency(row.w1Value)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: (showPercent ? row.m1Pct : row.m1Value) > 0 ? 'success.main' : (showPercent ? row.m1Pct : row.m1Value) < 0 ? 'error.main' : 'text.primary' }}>
                        {showPercent ? formatPercent(row.m1Pct) : formatCurrency(row.m1Value)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: (showPercent ? row.d90Pct : row.d90Value) > 0 ? 'success.main' : (showPercent ? row.d90Pct : row.d90Value) < 0 ? 'error.main' : 'text.primary' }}>
                        {showPercent ? formatPercent(row.d90Pct) : formatCurrency(row.d90Value)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: (showPercent ? row.m6Pct : row.m6Value) > 0 ? 'success.main' : (showPercent ? row.m6Pct : row.m6Value) < 0 ? 'error.main' : 'text.primary' }}>
                        {showPercent ? formatPercent(row.m6Pct) : formatCurrency(row.m6Value)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: (showPercent ? row.y1Pct : row.y1Value) > 0 ? 'success.main' : (showPercent ? row.y1Pct : row.y1Value) < 0 ? 'error.main' : 'text.primary' }}>
                        {showPercent ? formatPercent(row.y1Pct) : formatCurrency(row.y1Value)}
                      </TableCell>
                      <TableCell align="right">
                        {row.week52High ? formatCurrency(row.week52High) : '—'}
                      </TableCell>
                      <TableCell align="right">
                        {row.week52Low ? formatCurrency(row.week52Low) : '—'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default WatchlistPage;
